import React from 'react';
// ① hooks up Chart.js’s date‑fns adapter for the time scale
import 'chartjs-adapter-date-fns';

import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from 'chart.js';
import sourceColors from '../sourceColors.json';

// ② register the time scale and basic elements
ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

export default function FeedToPlantTonnageChart({
  data = [],
  height = 160,
  targetLines = [],
}) {
  // map each row into { x: Date, y: Number, source: String }
  const dataPoints = data.map(d => ({
    x: new Date(d.timestamp),
    y: d.value ?? d['100-WIT-1001'],
    source: d.source
  }));

  const chartData = {
    datasets: [
      {
        label: 'Feed to Plant Tonnage',
        data: dataPoints,
        // ③ remove any fill/shadow and default border
        fill: false,
        borderColor: 'transparent',
        borderWidth: 2,
        segment: {
          borderColor: ctx => {
            const idx = ctx.p1DataIndex;
            return sourceColors[dataPoints[idx].source] || '#333';
          }
        },
        pointRadius: 3
      }
    ]
  };

  const options = {
    responsive: true,
    // ④ allow the chart to fill its container rather than enforce an aspect ratio
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'hour',
          tooltipFormat: 'yyyy-MM-dd HH:mm',
          displayFormats: {
            hour: 'HH:mm'
          }
        },
      },
      y: {
        title: { display: true, text: 'Tonnage' }
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'index', intersect: false },
      targetLines: { lines: targetLines }
    }
  };

  const targetLinePlugin = {
    id: 'targetLines',
    beforeDraw: chart => {
      const {
        ctx,
        chartArea: { left, right },
      } = chart;
      const yScale = chart.scales.y;
      const opts = chart.options.plugins.targetLines || {};
      (opts.lines || []).forEach(tl => {
        const y = yScale.getPixelForValue(tl.value);
        ctx.save();
        ctx.strokeStyle = tl.color;
        ctx.setLineDash([2, 2]);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(left, y);
        ctx.lineTo(right, y);
        ctx.stroke();
        if (tl.label) {
          ctx.fillStyle = tl.color;
          ctx.font = '10px sans-serif';
          ctx.textBaseline = 'bottom';
          ctx.fillText(tl.label, right - ctx.measureText(tl.label).width, y - 2);
        }
        ctx.restore();
      });
    },
  };

  // ⑤ wrap in a fixed-height container so the panel size never changes
  return (
    <div style={{ width: '100%', height }}>
      <Line data={chartData} options={options} plugins={[targetLinePlugin]} />
    </div>
  );
}
